import { useToast } from "@chakra-ui/toast";
import { FormikHelpers, setIn } from "formik";
import { FC, useCallback } from "react";
import { graphql, useLazyLoadQuery } from "react-relay";
import { useNavigate } from "react-router-dom";

import { FunnelImportContainer_Mutation } from "~/src/__relay_artifacts__/FunnelImportContainer_Mutation.graphql";
import { FunnelImportContainer_Query } from "~/src/__relay_artifacts__/FunnelImportContainer_Query.graphql";
import { useMutationCommit } from "~/src/lib/react-relay";
import {
  formatUnknownError,
  summarizeImportRowFailureMessages,
} from "~/src/lib/utils/fomatUnknownError";

import {
  getFormPageFilteredToOriginalIndices,
  rowValuesToInput,
} from "./convertFunctions";
import { FunnelImportForm } from "./presentations";
import { serverFieldToFormikField } from "./serverFieldToFormikField";
import {
  FunnelImportFormValues,
  FunnelImportRowValues,
} from "./validationSchema";

export type Props = {
  siteSlug: string;
  initialFunnels?: FunnelImportRowValues[];
};

const query = graphql`
  query FunnelImportContainer_Query($siteSlug: String!) {
    site(slug: $siteSlug) {
      id
    }
  }
`;

const mutation = graphql`
  mutation FunnelImportContainer_Mutation($input: ImportFunnelsInput!) {
    importFunnels(input: $input) {
      results {
        rowIndex
        success
        errors {
          field
          message
        }
        funnel {
          id
          slug
          name
        }
      }
    }
  }
`;

export const FunnelImportContainer: FC<Props> = ({
  siteSlug,
  initialFunnels,
}) => {
  const toast = useToast();
  const navigate = useNavigate();
  const { site } = useLazyLoadQuery<FunnelImportContainer_Query>(query, {
    siteSlug,
  });

  const mutate = useMutationCommit<FunnelImportContainer_Mutation>(mutation);

  const handleSubmit = useCallback(
    async (
      values: FunnelImportFormValues,
      { setValues, setErrors }: FormikHelpers<FunnelImportFormValues>
    ) => {
      try {
        const formPageMaps = values.funnels.map(
          getFormPageFilteredToOriginalIndices
        );

        const res = await mutate({
          variables: {
            input: {
              siteId: site.id,
              funnels: values.funnels.map(rowValuesToInput),
            },
          },
        });

        const results = res.importFunnels?.results ?? [];
        const successIndices = new Set(
          results.filter((r) => r.success).map((r) => r.rowIndex)
        );
        const failedResults = results.filter((r) => !r.success);

        const successCount = successIndices.size;
        const failCount = failedResults.length;

        // Remove successful rows, keep failed rows
        const remainingFunnels = values.funnels.filter(
          (_, i) => !successIndices.has(i)
        );

        // Build field-level errors for remaining rows
        if (failedResults.length > 0) {
          // Map old rowIndex to new index in remaining array
          const oldToNewIndex: Map<number, number> = new Map();
          let newIdx = 0;
          values.funnels.forEach((_, oldIdx) => {
            if (!successIndices.has(oldIdx)) {
              oldToNewIndex.set(oldIdx, newIdx);
              newIdx++;
            }
          });

          const funnelErrors: Record<string, unknown>[] = remainingFunnels.map(
            () => ({})
          );

          failedResults.forEach((result) => {
            const newIndex = oldToNewIndex.get(result.rowIndex);
            if (newIndex === undefined) return;

            const formPageIndexMap = formPageMaps[result.rowIndex];
            if (result.errors.length > 0) {
              // Nested shape so getIn(errors, "funnels[i].thanksPage.url") resolves (flat
              // keys like { "thanksPage.url": "…" } do not).
              let rowErrors: Record<string, unknown> = {};
              result.errors.forEach((err) => {
                const relPath = serverFieldToFormikField(err.field, {
                  formPageIndexMap,
                });
                rowErrors = setIn(rowErrors, relPath, err.message) as Record<
                  string,
                  unknown
                >;
              });
              funnelErrors[newIndex] = rowErrors;
            } else {
              funnelErrors[newIndex] = {
                name: result.errors.map((e) => e.message).join(", "),
              };
            }
          });

          setErrors({ funnels: funnelErrors as never });
        }

        await setValues({ funnels: remainingFunnels });

        if (successCount > 0) {
          toast({
            title: `${successCount}件のファネルをインポートしました`,
            status: "success",
          });
        }
        if (failCount > 0) {
          const failureDetail =
            summarizeImportRowFailureMessages(failedResults);
          toast({
            title: `${failCount}件のファネルでエラーが発生しました。内容を確認して再送信してください。`,
            ...(failureDetail ? { description: failureDetail } : {}),
            status: "error",
            duration: 9000,
            isClosable: true,
          });
        }

        if (failCount === 0 && successCount > 0) {
          navigate(`/sites/${siteSlug}/settings?tab=funnels`, {
            state: { refetchFunnelList: true },
          });
        }
      } catch (err) {
        toast({
          title: "インポートに失敗しました",
          description: formatUnknownError(err),
          status: "error",
          duration: 9000,
          isClosable: true,
        });
      }
    },
    [mutate, navigate, site.id, siteSlug, toast]
  );

  return (
    <FunnelImportForm
      siteSlug={siteSlug}
      initialFunnels={initialFunnels}
      onSubmit={handleSubmit}
    />
  );
};
